import React, { useState } from "react"
import StatusChip from "./StatusChip"
import Avatar from "./Avatar"
import { fmt, dateFmt } from "../helpers/format"
import { usePanorama } from "../context/PanoramaContext"
import Button from "@react/shared/ui/Button"
import ActionButton from "./ActionButton"

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

// Flattens the task tree into select options (leaves with their path).
const flattenTasks = (tasks, path = []) =>
  (tasks || []).flatMap(task =>
    task.children && task.children.length > 0
      ? flattenTasks(task.children, [...path, task.title])
      : [{ id: task.id, label: [...path, task.title].join(" › ") }]
  )

// Modal for creating a work order attached to the project (and to a task).
const NewWorkorderModal = ({ project, tasks, onClose }) => {
  const today = new Date().toISOString().slice(0, 10)
  const [title, setTitle]       = useState("")
  const [taskId, setTaskId]     = useState("")
  const [date, setDate]         = useState(today)
  const [duration, setDuration] = useState(60)
  const [error, setError]       = useState(null)
  const [saving, setSaving]     = useState(false)

  const taskOptions = flattenTasks(tasks)

  const submit = () => {
    if (!title.trim()) { setError(I18n.t("projects.interventions.errors.title_required")); return }
    setSaving(true)
    setError(null)
    fetch(`/projects/${project.id}/workorders.json`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf() },
      credentials: "same-origin",
      body: JSON.stringify({
        title,
        project_task_id:    taskId || null,
        date_planned:       date,
        predicted_duration: duration,
      }),
    })
      .then(response => response.json())
      .then(payload => {
        if (payload.ok) window.location.href = payload.path
        else { setError(payload.error || I18n.t("projects.interventions.errors.create")); setSaving(false) }
      })
      .catch(() => { setError(I18n.t("projects.interventions.errors.create")); setSaving(false) })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-[480px] p-5" onClick={e => e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.interventions.modal.title")}</h3>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          {I18n.t("projects.interventions.modal.subtitle", { project: project.name })}
        </p>

        <label className="block mt-4 text-[12px] font-medium text-slate-600">{I18n.t("projects.interventions.modal.title_label")}</label>
        <input
          type="text"
          autoFocus
          className="mt-1 w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-200"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />

        <label className="block mt-3 text-[12px] font-medium text-slate-600">{I18n.t("projects.interventions.modal.task")}</label>
        <select
          className="mt-1 w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[13px] bg-white"
          value={taskId}
          onChange={e => setTaskId(e.target.value)}
        >
          <option value="">{I18n.t("projects.interventions.modal.no_task")}</option>
          {taskOptions.map(option => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>

        <div className="mt-3 flex gap-3">
          <div className="flex-1">
            <label className="block text-[12px] font-medium text-slate-600">{I18n.t("projects.interventions.modal.date")}</label>
            <input
              type="date"
              className="mt-1 w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[13px]"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
          </div>
          <div className="w-36">
            <label className="block text-[12px] font-medium text-slate-600">{I18n.t("projects.interventions.modal.duration")}</label>
            <input
              type="number"
              min="15"
              step="15"
              className="mt-1 w-full border border-slate-200 rounded-lg px-2.5 py-2 text-[13px]"
              value={duration}
              onChange={e => setDuration(e.target.value)}
            />
          </div>
        </div>

        {error && <p className="mt-3 text-[12.5px] text-rose-600">{error}</p>}

        {/* Le pied du dialogue passe par le bouton partagé : il portait le
            même bleu-600 hors-charte que celui de l'en-tête. */}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="md" onClick={onClose}>
            {I18n.t("projects.interventions.modal.cancel")}
          </Button>
          <Button variant="primary" size="md" disabled={saving} onClick={submit}>
            {I18n.t("projects.interventions.modal.submit")}
          </Button>
        </div>
      </div>
    </div>
  )
}

const Hours = ({ actual, planned }) => {
  const isOver = planned > 0 && actual > planned * 1.05
  const unit    = I18n.t("projects.units.hour_short")
  const planTxt = planned ? `${planned} ${unit}` : "—"
  const actTxt  = actual  ? `${actual} ${unit}`  : "—"
  return (
    <span className={`text-[12px] tabular-nums ${isOver ? "text-rose-600 font-medium" : "text-slate-600"}`}>
      {actTxt} <span className="text-slate-400">/</span> {planTxt}
    </span>
  )
}

const GroupHeader = ({ group }) => {
  const completion = Math.round(group.task_completion || 0)
  const summary    = I18n.t("projects.interventions.group.count", { count: group.count })
                   + (group.completed ? ` · ${I18n.t("projects.interventions.group.completed", { count: group.completed })}` : "")
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50/70 border-b border-slate-100">
      <span className="text-[13px] font-medium text-slate-800 truncate">{group.task_title}</span>
      <span className="text-[12px] text-slate-500 tabular-nums">· {summary}</span>
      <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 text-blue-700 ring-1 ring-blue-100 rounded-full text-[11.5px] font-medium tabular-nums">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
        {completion}%
      </span>
    </div>
  )
}

const Row = ({ workorder }) => (
  <a
    href={workorder.path || "#"}
    className="grid items-center gap-3 px-4 py-2.5 hover:bg-slate-50/60"
    style={{ gridTemplateColumns: "120px minmax(180px,1fr) 150px 110px 130px 130px 110px" }}
  >
    <div className="text-[11px] uppercase tracking-wider text-slate-500 tabular-nums font-medium">{workorder.number}</div>
    <div className="text-[13px] text-slate-800 truncate">{workorder.title || "—"}</div>
    <div className="flex items-center gap-1.5 min-w-0">
      <Avatar name={workorder.technician?.name} size="sm" ring={false} />
      <span className="text-[12px] text-slate-700 truncate">{workorder.technician?.name || "—"}</span>
    </div>
    <div className="text-[12px] text-slate-500 tabular-nums">{dateFmt(workorder.scheduled_at)}</div>
    <div><StatusChip status={workorder.status} label={workorder.status_label} dot /></div>
    <div className="text-right"><Hours actual={workorder.actual_hours} planned={workorder.planned_hours} /></div>
    <div className="text-right text-[12.5px] tabular-nums text-slate-700 font-medium">{fmt(workorder.cost)}</div>
  </a>
)

const InterventionsSection = ({ interventions, project }) => {
  const { state } = usePanorama()
  const [creating, setCreating] = useState(false)
  const isEmpty = !interventions || interventions.length === 0

  return (
    <section id="section-interventions" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.interventions.title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {I18n.t("projects.interventions.subtitle")}
          </p>
        </div>
        <ActionButton onClick={() => setCreating(true)}>
          {I18n.t("projects.interventions.actions.new")}
        </ActionButton>
      </header>

      {creating && (
        <NewWorkorderModal
          project={project}
          tasks={state.tasks}
          onClose={() => setCreating(false)}
        />
      )}

      {isEmpty ? (
        <div className="bg-white border border-slate-200 rounded-xl px-6 py-12 text-center text-[13px] text-slate-500">
          {I18n.t("projects.interventions.empty")}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
          {interventions.map(group => (
            <div key={group.task_id || "none"}>
              <GroupHeader group={group} />
              {group.workorders.map(w => <Row key={w.id} workorder={w} />)}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

export default InterventionsSection
